<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Under_the_Hood/Editor_Framework/Predefined_Boxes';
    let nextLink= '/Documentation/Under_the_Hood/Core_Interfaces';
    
    import Note from "$lib/notes/Note.svelte";
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="external-component-box-types-1"
  bind:intersecting={visibleById["external-component-box-types-1"]}
  onIntersectChange={(visible) => setVisible("external-component-box-types-1", visible)}
>External Component Box Types</SectionComponent>
<Note>{#snippet header()}External components are fully supported{/snippet}
{#snippet content()}
<p>External components are a fully supported part of the editor framework. While compatibility with every third-party library cannot be guaranteed, the APIs described here are stable.</p>
{/snippet}
</Note>
<p>You can include UI components that are not native to Freon.<br>
See <strong><a href="/Documentation/Customizations/External_Components">External Components</a></strong> for an overview.</p>
<p>Using external components requires knowledge of the Freon <strong><a href="/Documentation/Under_the_Hood/Editor_Framework">Box Model</a></strong>.<br>
This page documents the <strong>API</strong> for the various external-component box types.</p>
<hr>
<SectionComponent tag="h2" 
  id="simple-additions-2"
  bind:intersecting={visibleById["simple-additions-2"]}
  onIntersectChange={(visible) => setVisible("simple-additions-2", visible)}
>Simple Additions</SectionComponent>
<p>Simple additions have <strong>no link to the model (AST)</strong> and may appear anywhere in a projection.<br>
They are still associated with the node whose projection they are placed in.</p>
<ul>
<li><strong>Syntax:</strong> <code>[external=&lt;ComponentName&gt;]</code> (no space after <code>[</code>).</li>
<li>The component name must be declared in the <strong><a href="/Documentation/Defining_an_Editor/Global_Projections"><code>global</code></a></strong> section of the default editor.</li>
<li><strong>Box type:</strong> <code>SimpleExternalBox</code></li>
<li><strong>Interface:</strong> <em>(no specific API beyond common external props and <code>findParam()</code>)</em></li>
</ul>
<p>Example:</p>
<pre class="language-freon"><code class="language-freon">[external=AnimatedGif number="1"]</code></pre>
<SectionComponent tag="h2" 
  id="fragment-wrappers-3"
  bind:intersecting={visibleById["fragment-wrappers-3"]}
  onIntersectChange={(visible) => setVisible("fragment-wrappers-3", visible)}
>Fragment Wrappers</SectionComponent>
<p>A <strong>fragment wrapper</strong> surrounds a single projection (the <strong>child box</strong>).<br>
The <code>childBox</code> itself may be a vertical or horizontal layout containing many other boxes.</p>
<ul>
<li><strong>Syntax (positioning):</strong> <code>[fragment &lt;NAME&gt; wrap=&lt;ExternalComponent&gt;]</code></li>
<li><strong>Define wrapped content:</strong> Provide the fragment body in the fragment’s own bracketed section.</li>
<li><strong>Box type:</strong> <code>FragmentWrapperBox</code></li>
<li><strong>Interface:</strong>
<ul>
<li><code>childBox: Box</code> — the wrapped projection; render it with <code>RenderComponent</code>.</li>
</ul>
</li>
</ul>
<p>Example:</p>
<pre class="language-freon"><code class="language-freon">MyConcept &#123;
    [
        Here is a fragment [fragment XX wrap=FB_Card]
    ]
    fragment XX [
        My First Card wrapping a property: $&#123;self.isUnderConstruction&#125;
        Great, isn't it!
    ]
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="property-projections-wrapping-vs-replacing-4"
  bind:intersecting={visibleById["property-projections-wrapping-vs-replacing-4"]}
  onIntersectChange={(visible) => setVisible("property-projections-wrapping-vs-replacing-4", visible)}
>Property Projections: Wrapping vs. Replacing</SectionComponent>
<p>A property projection can be <strong>wrapped</strong> by an external component, or the external component can <strong>replace</strong> the native projection.<br>
For <strong>replacers</strong>, you are responsible for reading/writing the property value and handling keyboard/tab behavior.</p>
<SectionComponent tag="h2" 
  id="wrapping-primitive-properties-5"
  bind:intersecting={visibleById["wrapping-primitive-properties-5"]}
  onIntersectChange={(visible) => setVisible("wrapping-primitive-properties-5", visible)}
>Wrapping Primitive Properties</SectionComponent>
<ul>
<li><strong>Syntax:</strong> <code>$&#123;self.name wrap=FB_Dialog&#125;</code></li>
<li><strong>Box types:</strong> <code>StringWrapperBox</code>, <code>NumberWrapperBox</code>, <code>BooleanWrapperBox</code></li>
<li><strong>Interface:</strong>
<ul>
<li><code>getPropertyName(): string</code></li>
<li><code>getPropertyValue(): string | number | boolean</code> — type depends on the wrapper box.</li>
<li><code>childBox: Box</code> — the native projection for the property (or for the entire list if wrapping a list).</li>
</ul>
</li>
</ul>
<SectionComponent tag="h2" 
  id="wrapping-part-list-properties-6"
  bind:intersecting={visibleById["wrapping-part-list-properties-6"]}
  onIntersectChange={(visible) => setVisible("wrapping-part-list-properties-6", visible)}
>Wrapping Part List Properties</SectionComponent>
<ul>
<li><strong>Syntax:</strong> <code>$&#123;self.parts wrap=FB_Accordion&#125;</code></li>
<li><strong>Box type:</strong> <code>PartListWrapperBox</code></li>
<li><strong>Interface:</strong>
<ul>
<li><code>getPropertyName(): string</code></li>
<li><code>getPropertyValue(): FreNode[]</code> — cast as needed.</li>
<li><code>childBox: Box</code> — the native projection for the whole list (horizontal/vertical/table).</li>
</ul>
</li>
</ul>
<SectionComponent tag="h2" 
  id="wrapping-reference-list-properties-7"
  bind:intersecting={visibleById["wrapping-reference-list-properties-7"]}
  onIntersectChange={(visible) => setVisible("wrapping-reference-list-properties-7", visible)}
>Wrapping Reference List Properties</SectionComponent>
<ul>
<li><strong>Syntax:</strong> <code>$&#123;self.parts wrap=FB_Accordion&#125;</code></li>
<li><strong>Box type:</strong> <code>RefListWrapperBox</code></li>
<li><strong>Interface:</strong>
<ul>
<li><code>getPropertyName(): string</code></li>
<li><code>getPropertyValue(): FreNodeReference[]</code></li>
<li><code>childBox: Box</code> — the native projection for the whole list (horizontal/vertical/table).</li>
</ul>
</li>
</ul>
<SectionComponent tag="h2" 
  id="wrapping-part-properties-8"
  bind:intersecting={visibleById["wrapping-part-properties-8"]}
  onIntersectChange={(visible) => setVisible("wrapping-part-properties-8", visible)}
>Wrapping Part Properties</SectionComponent>
<ul>
<li><strong>Syntax:</strong> <code>$&#123;self.part wrap=FB_Dialog&#125;</code></li>
<li><strong>Box type:</strong> <code>PartWrapperBox</code></li>
<li><strong>Interface:</strong>
<ul>
<li><code>getPropertyName(): string</code></li>
<li><code>getPropertyValue(): FreNode</code> — cast as needed.</li>
<li><code>childBox: Box</code> — render with <code>RenderComponent</code>.</li>
</ul>
</li>
</ul>
<SectionComponent tag="h2" 
  id="wrapping-reference-properties-9"
  bind:intersecting={visibleById["wrapping-reference-properties-9"]}
  onIntersectChange={(visible) => setVisible("wrapping-reference-properties-9", visible)}
>Wrapping Reference Properties</SectionComponent>
<ul>
<li><strong>Syntax:</strong> <code>$&#123;self.reference wrap=FB_Dialog&#125;</code></li>
<li><strong>Box type:</strong> <code>RefWrapperBox</code></li>
<li><strong>Interface:</strong>
<ul>
<li><code>getPropertyName(): string</code></li>
<li><code>getPropertyValue(): FreNodeReference</code></li>
<li><code>childBox: Box</code> — render with <code>RenderComponent</code>.</li>
</ul>
</li>
</ul>
<SectionComponent tag="h2" 
  id="replacing-primitive-properties-10"
  bind:intersecting={visibleById["replacing-primitive-properties-10"]}
  onIntersectChange={(visible) => setVisible("replacing-primitive-properties-10", visible)}
>Replacing Primitive Properties</SectionComponent>
<ul>
<li><strong>Syntax:</strong> <code>$&#123;self.name replace=FB_Dialog&#125;</code></li>
<li><strong>Box types:</strong> <code>StringReplacerBox</code>, <code>NumberReplacerBox</code>, <code>BooleanReplacerBox</code></li>
<li><strong>Interface:</strong>
<ul>
<li><code>getPropertyName(): string</code></li>
<li><code>getPropertyValue(): string | number | boolean</code></li>
<li><code>setPropertyValue(newValue: string | number | boolean)</code></li>
</ul>
</li>
</ul>
<SectionComponent tag="h2" 
  id="replacing-part-list-properties-11"
  bind:intersecting={visibleById["replacing-part-list-properties-11"]}
  onIntersectChange={(visible) => setVisible("replacing-part-list-properties-11", visible)}
>Replacing Part List Properties</SectionComponent>
<ul>
<li><strong>Syntax:</strong> <code>$&#123;self.parts replace=FB_Accordion&#125;</code></li>
<li><strong>Box type:</strong> <code>PartListReplacerBox</code></li>
<li><strong>Interface:</strong>
<ul>
<li><code>getPropertyName(): string</code></li>
<li><code>getPropertyValue(): FreNode[]</code></li>
<li><code>setPropertyValue(newValue: FreNode[])</code></li>
<li><code>children: Box[]</code> — one box per list element.</li>
</ul>
</li>
</ul>
<SectionComponent tag="h2" 
  id="replacing-reference-list-properties-12"
  bind:intersecting={visibleById["replacing-reference-list-properties-12"]}
  onIntersectChange={(visible) => setVisible("replacing-reference-list-properties-12", visible)}
>Replacing Reference List Properties</SectionComponent>
<ul>
<li><strong>Syntax:</strong> <code>$&#123;self.parts replace=FB_Accordion&#125;</code></li>
<li><strong>Box type:</strong> <code>RefListReplacerBox</code></li>
<li><strong>Interface:</strong>
<ul>
<li><code>getPropertyName(): string</code></li>
<li><code>getPropertyValue(): FreNodeReference[]</code></li>
<li><code>setPropertyValue(newValue: FreNodeReference[])</code></li>
<li><code>children: Box[]</code> — one box per list element.</li>
</ul>
</li>
</ul>
<SectionComponent tag="h2" 
  id="replacing-part-properties-13"
  bind:intersecting={visibleById["replacing-part-properties-13"]}
  onIntersectChange={(visible) => setVisible("replacing-part-properties-13", visible)}
>Replacing Part Properties</SectionComponent>
<ul>
<li><strong>Syntax:</strong> <code>$&#123;self.part replace=FB_Dialog&#125;</code></li>
<li><strong>Box type:</strong> <code>PartReplacerBox</code></li>
<li><strong>Interface:</strong>
<ul>
<li><code>getPropertyName(): string</code></li>
<li><code>getPropertyValue(): FreNode</code></li>
<li><code>setPropertyValue(newValue: FreNode)</code></li>
</ul>
</li>
</ul>
<SectionComponent tag="h2" 
  id="replacing-reference-properties-14"
  bind:intersecting={visibleById["replacing-reference-properties-14"]}
  onIntersectChange={(visible) => setVisible("replacing-reference-properties-14", visible)}
>Replacing Reference Properties</SectionComponent>
<ul>
<li><strong>Syntax:</strong> <code>$&#123;self.reference replace=FB_Dialog&#125;</code></li>
<li><strong>Box type:</strong> <code>RefReplacerBox</code></li>
<li><strong>Interface:</strong>
<ul>
<li><code>getPropertyName(): string</code></li>
<li><code>getPropertyValue(): FreNodeReference</code></li>
<li><code>setPropertyValue(newValue: FreNodeReference)</code></li>
</ul>
</li>
</ul>
<SectionComponent tag="h2" 
  id="wiring-15"
  bind:intersecting={visibleById["wiring-15"]}
  onIntersectChange={(visible) => setVisible("wiring-15", visible)}
>Wiring</SectionComponent>
<ol>
<li>
<p><strong>Create your Svelte components.</strong><br>
Identify which <strong>box type</strong> your component will integrate with, and use that box’s interface to get/set model values.</p>
</li>
<li>
<p><strong>Declare components in the editor definition.</strong><br>
Add names in the <code>global</code> section of the default editor (see <strong><a
  href="/Documentation/Defining_an_Editor/Global_Projections"
>Global Projections</a></strong>).</p>
<p>Example:</p>
<pre class="language-freon"><code class="language-freon">global &#123;
    external &#123;
        AnimatedGif,
        FB_Card,
        FB_Accordion,
        FB_Dialog,
        DatePicker
    &#125;
&#125;</code></pre>
</li>
<li>
<p><strong>Register components at runtime.</strong><br>
Call <code>setCustomComponents()</code> from <code>@freon4dsl/core-svelte</code> <strong>before</strong> launching the app.<br>
Names <strong>must match</strong> those declared in step 2.</p>
<p>Example:</p>
<pre class="language-ts"><code class="language-ts">setCustomComponents([
   &#123; component: ShowAnimatedGif,    knownAs: "AnimatedGif" &#125;,
   &#123; component: FB_Card_Component,  knownAs: "FB_Card" &#125;,
   &#123; component: FB_Accordion,       knownAs: "FB_Accordion" &#125;,
   &#123; component: FB_Dialog,          knownAs: "FB_Dialog" &#125;,
   &#123; component: DatePicker,         knownAs: "DatePicker" &#125;
]);</code></pre>
</li>
</ol>
<SectionComponent tag="h2" 
  id="external-component-parameters-16"
  bind:intersecting={visibleById["external-component-parameters-16"]}
  onIntersectChange={(visible) => setVisible("external-component-parameters-16", visible)}
>External Component Parameters</SectionComponent>
<p>You can pass parameters to an external component from the <code>.edit</code> file as simple <strong>key-value</strong> pairs (both strings).<br>
All external-component box types provide:</p>
<ul>
<li><code>findParam(key: string): string</code> — returns the parameter value (if present).</li>
</ul>


<PrevNextSection {prevLink} {nextLink} />